import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { articleApi } from "api/articleApi";

export const fetchArticles = createAsyncThunk(
  "articles/fetchArticles",
  async (_, { rejectWithValue }) => {
    try {
      return await articleApi.fetchArticles();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchArticle = createAsyncThunk(
  "articles/fetchArticle",
  async ({ hash, userSide }, { rejectWithValue }) => {
    try {
      return await articleApi.fetchArticle({ hash, userSide });
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Обновление статьи
export const updateArticle = createAsyncThunk(
  "articles/updateArticle",
  async (formData, { rejectWithValue }) => {
    try {
      return await articleApi.updateArticle(formData);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Создание статьи
export const createArticle = createAsyncThunk(
  "articles/createArticle",
  async (formData, { rejectWithValue }) => {
    try {
      return await articleApi.createArticle(formData);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Удаление статьи
export const deleteArticle = createAsyncThunk(
  "articles/deleteArticle",
  async (hash, { rejectWithValue }) => {
    try {
      return await articleApi.deleteArticle(hash); // возвращаем hash удаленной статьи
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Получение HTML статьи
export const fetchArticleHTML = createAsyncThunk(
  "articles/fetchArticleHTML",
  async (hash, { rejectWithValue }) => {
    try {
      return await articleApi.fetchArticleHTML(hash);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const articlesSlice = createSlice({
  name: "articles",
  initialState: {
    items: [],
    currentArticle: null,
    currentArticleHTML: null,
    status: "idle",
    error: null,
    loading: false,
    htmlLoading: false,
    htmlError: null,
  },
  reducers: {
    clearCurrentArticle: (state) => {
      state.currentArticle = null;
    },
    clearCurrentArticleHTML: (state) => {
      state.currentArticleHTML = null;
      state.htmlError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Получение списка статей
      .addCase(fetchArticles.pending, (state) => {
        state.status = "loading";
        state.currentArticle = null;
      })
      .addCase(fetchArticles.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.items = action.payload;
        state.currentArticle = null;
      })
      .addCase(fetchArticles.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error.message;
        state.currentArticle = null;
      })

      // Получение одной статьи
      .addCase(fetchArticle.pending, (state) => {
        state.loading = true;
        state.currentArticle = null;
        state.error = null;
      })
      .addCase(fetchArticle.fulfilled, (state, action) => {
        state.loading = false;
        state.currentArticle = action.payload;
      })
      .addCase(fetchArticle.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })

      // Обновление статьи
      .addCase(updateArticle.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updateArticle.fulfilled, (state, action) => {
        state.status = "succeeded";
        const index = state.items.findIndex(
          (item) => item.hash === action.payload.hash
        );
        if (index !== -1) {
          state.items[index] = { ...state.items[index], ...action.payload };
        }
      })
      .addCase(updateArticle.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Создание статьи
      .addCase(createArticle.pending, (state) => {
        state.status = "loading";
      })
      .addCase(createArticle.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.items.push(action.payload);
      })
      .addCase(createArticle.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Удаление статьи
      .addCase(deleteArticle.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteArticle.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.items = state.items.filter(
          (item) => item.hash !== action.payload
        );
      })
      .addCase(deleteArticle.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Получение HTML статьи
      .addCase(fetchArticleHTML.pending, (state) => {
        state.htmlLoading = true;
        state.currentArticleHTML = null;
        state.htmlError = null;
      })
      .addCase(fetchArticleHTML.fulfilled, (state, action) => {
        state.htmlLoading = false;
        state.currentArticleHTML = action.payload;
      })
      .addCase(fetchArticleHTML.rejected, (state, action) => {
        state.htmlLoading = false;
        state.htmlError = action.payload || action.error.message;
      });
  },
});

export const { clearCurrentArticle, clearCurrentArticleHTML } = articlesSlice.actions;
export default articlesSlice.reducer;
